import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";

import clsx from "clsx";
import { differenceBy } from "lodash";

import {
  LEVEL_TO_NUMBER,
  NUMBER_TO_LEVEL,
} from "@features/overview-v2/components/tile/utils";
import { type Level } from "@features/overview-v2/types";
import {
  FloatingPortal,
  flip,
  safePolygon,
  useDelayGroup,
  useFloating,
  useHover,
  useInteractions,
} from "@floating-ui/react";
import { useFuzzySearch } from "@hooks/use-fuzzy-search";
import { type CategoricalBreakdownConfig } from "@north-beam/nb-common";
import { TertiaryButton } from "@shared/buttons";
import { Checkbox } from "@shared/checkbox";
import { useVirtualizer } from "@tanstack/react-virtual";

interface Props {
  style?: CSSProperties;
  row: CategoricalBreakdownConfig;
  selectedValues: string[];
  onChange(values: string[]): void;
  onReset?(): void;
  excludeNotSet?: boolean;
  labelsByLevel?: Record<string, Level>;
}

export const OptionWithSubmenu = ({
  style,
  row,
  selectedValues,
  onChange,
  onReset,
  excludeNotSet = false,
  labelsByLevel,
}: Props) => {
  const scrollElRef = useRef<HTMLDivElement>(null);

  const [isOpen, setIsOpen] = useState(false);

  const choices = excludeNotSet
    ? row.choices.filter((v) => v.value)
    : row.choices;

  const [filteredOptions, { search, setSearch, clearSearch }] = useFuzzySearch(
    choices,
    {
      searchKeys: ["label"],
    },
  );

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    transform: false,
    placement: "right-start",
    middleware: [flip()],
  });

  const { delay } = useDelayGroup(context);

  const hover = useHover(context, {
    delay,
    handleClose: safePolygon({
      requireIntent: false,
      blockPointerEvents: true,
      buffer: 1,
    }),
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover]);

  useEffect(() => {
    if (isOpen) {
      clearSearch();
    }
  }, [isOpen, clearSearch]);

  const rowVirtualizer = useVirtualizer({
    count: filteredOptions.length,
    getScrollElement: () => scrollElRef.current,
    estimateSize: () => 40,
    overscan: 5,
  });

  const isAllSelected =
    differenceBy(
      choices.map((e) => e.value),
      selectedValues,
    ).length === 0;

  const selectAllRef = useCallback(
    (node: HTMLInputElement) => {
      if (node !== null) {
        node.indeterminate = selectedValues.length > 0 && !isAllSelected;
      }
    },
    [selectedValues, isAllSelected],
  );

  const breakdownMaxAllowedLevel = useMemo(() => {
    if (!labelsByLevel) {
      return "campaign";
    }

    let result = 0;

    row.choices.forEach((choice) => {
      const lvl =
        labelsByLevel[`${row.key}:${choice.value}`] ??
        labelsByLevel[`${row.key}:*`] ??
        3;
      const lvlNumber = LEVEL_TO_NUMBER[lvl];

      if (result < lvlNumber) {
        result = lvlNumber;
      }
    });

    return NUMBER_TO_LEVEL[result];
  }, [row, labelsByLevel]);

  return (
    <div>
      <div
        style={style}
        className={clsx(
          "w-full text-left px-4 hover:bg-cosmic-4 h-10 flex items-center cursor-pointer",
          "absolute top-0 left-0 floatingui-item",
          { "bg-cosmic-4": isOpen },
        )}
        ref={refs.setReference}
        {...getReferenceProps()}
      >
        <div className="flex-1 truncate">
          <span>{row.name}</span>
          <div className="flex items-center gap-1 text-xxs text-primary">
            {breakdownMaxAllowedLevel !== "campaign" && (
              <div>
                {breakdownMaxAllowedLevel === "ad" ? "Ad" : "Adset, Ad"}
              </div>
            )}
            {breakdownMaxAllowedLevel !== "campaign" &&
              selectedValues.length > 0 && <div>&bull;</div>}
            {selectedValues.length > 0 && (
              <div>
                {selectedValues.length} of {choices.length}
              </div>
            )}
          </div>
        </div>
        <i className={clsx("fa-regular fa-sharp fa-angle-right text-cosmic")} />
      </div>
      <FloatingPortal>
        {isOpen && (
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            className={clsx(
              "absolute z-[9998] bg-white font-sans rounded-lg elevation-1 text-sm pt-3",
              "pb-2 w-[250px]",
            )}
            {...getFloatingProps()}
          >
            <div className="nb-input-container relative px-4 mb-2">
              <input
                className="nb-input pr-8"
                type="text"
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <i
                className={clsx(
                  "fa-regular fa-sharp fa-magnifying-glass text-[0.9rem] text-primary",
                  "w-4 absolute right-[24px] top-[10px]",
                )}
              />
            </div>
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-2 text-disabled">NO RESULTS FOUND</div>
            ) : (
              <div
                className="px-4 hover:bg-cosmic-4 h-10 flex items-center cursor-pointer relative"
                onClick={() =>
                  onChange(isAllSelected ? [] : choices.map((e) => e.value))
                }
              >
                <Checkbox
                  ref={selectAllRef}
                  className="mr-2"
                  inputClassName={clsx(
                    "before:h-[inherit] before:w-[inherit] after:top-[15px] after:left-[18px]",
                  )}
                  value={isAllSelected}
                />
                <span className="font-bold">
                  {isAllSelected ? "Clear all" : "Select all"}
                </span>
              </div>
            )}
            <div ref={scrollElRef} className="max-h-[300px] overflow-y-auto">
              <div
                style={{
                  position: "relative",
                  height: `${rowVirtualizer.getTotalSize()}px`,
                }}
              >
                {rowVirtualizer.getVirtualItems().map((e) => {
                  const choice = filteredOptions[e.index];
                  let maxAllowedLevel: Level | null = null;

                  if (labelsByLevel) {
                    maxAllowedLevel =
                      labelsByLevel[`${row.key}:${choice.value}`] ??
                      labelsByLevel[`${row.key}:*`];
                  }

                  return (
                    <div
                      key={e.key}
                      className={clsx(
                        "px-4 hover:bg-cosmic-4 h-10 w-full flex items-center cursor-pointer",
                        "absolute top-0 left-0 w-full",
                      )}
                      style={{ transform: `translateY(${e.start}px)` }}
                      onClick={() => {
                        if (selectedValues.includes(choice.value)) {
                          onChange(
                            selectedValues.filter((v) => v !== choice.value),
                          );
                        } else {
                          onChange([...selectedValues, choice.value]);
                        }
                      }}
                    >
                      <Checkbox
                        className="mr-2"
                        value={selectedValues.includes(choice.value)}
                      />
                      <span className="flex flex-col gap-0.5 truncate">
                        <span className="leading-4 truncate">
                          {choice.label}
                        </span>
                        {maxAllowedLevel && maxAllowedLevel !== "campaign" && (
                          <span className="text-xxs text-cosmic">
                            Available only on{" "}
                            {maxAllowedLevel === "ad" ? "Ad" : "Ad and Adset"}{" "}
                            level
                          </span>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
            {onReset && (
              <div className="pt-4 px-4 shadow-[0px_-2px_2px_rgba(0,0,0,0.1)]">
                <TertiaryButton
                  className="w-full text-center border border-gray text-interstellar"
                  onClick={onReset}
                >
                  RESET
                </TertiaryButton>
              </div>
            )}
          </div>
        )}
      </FloatingPortal>
    </div>
  );
};
